import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchInjection } from "../../../store/slices/injectionsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function InjectionPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentInjection, isLoading, error } = useSelector(
    (state) => state.injections
  );

  useEffect(() => {
    if (id) dispatch(fetchInjection(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentInjection) {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      window.onafterprint = () => {
        window.close();
      };
      return () => clearTimeout(timer);
    }
  }, [currentInjection]);

  if (isLoading) return <div className="p-3">Loading...</div>;
  if (error) return <div className="p-3 text-danger">{error}</div>;
  if (!currentInjection || !currentInjection.patientInfo)
    return <div className="p-3">No injection data found</div>;

  const info = currentInjection.patientInfo;
  const hasFinanceInfo = info.amount || info.payment_status || info.reference_no || info.payment_date;

  // Mapping to the correct array keys from your JSON
  const onDuty = currentInjection.onDutyInjections?.[0] || {};
  const rn = currentInjection.rnInjections?.[0] || {};

  const formatDate = (iso) => (iso ? iso.split("T")[0] : " ");
  const formatTime = (iso) => {
    if (!iso) return " ";
    const parts = iso.split("T")[1];
    if (!parts) return " ";
    return parts.split(".")[0];
  };

  // Updated medicine list according to your form/response
  const medicineList = [
    { key: "quetiapine", label: "Tab Quetiapine", type: "Oral" },
    { key: "serenace", label: "Inj Serenace", type: "Injection" },
    { key: "avil", label: "Inj Avil", type: "Injection" },
    { key: "clopixol", label: "Inj Clopixol Accuphase", type: "Injection" },
    { key: "dormicum", label: "Inj Dormicum", type: "Injection" },
    { key: "vallium", label: "Inj Vallium", type: "Injection" },
    { key: "procyclidine", label: "Tab Procyclidine", type: "Oral" },
  ];

  return (
    <>
      <style jsx global>{`
        * { text-align: left !important; box-sizing: border-box !important; }
        body { font-family: Arial, sans-serif !important; line-height: 1.4 !important; color: #000 !important; background-color: #fff !important; margin: 0 !important; padding: 0 !important; }
        .print-container { display: block !important; width: 100% !important; padding: 20px !important; position: relative !important; }
        ${printFooterStyles}
        .logo { 
        position: absolute !important; 
        top: 5px !important; 
        left: 10px !important; 
        z-index: 1000 !important; 
          }
        .logo img { height: 60px !important; width: auto !important; }
        .header { 
          text-align: center !important; 
          margin-bottom: 15px !important; 
          padding-top: 15px !important; 
          border-bottom: 2px solid #383838 !important; 
          padding-bottom: 10px !important; 
            }
        .header h3 { font-size: 1.5em !important; font-weight: bold !important; text-align: center !important; border-bottom: 1.5px solid #383838 !important; padding-bottom: 10px !important; margin-bottom: 10px !important; }
        .header h6 { font-size: 1.1em !important; text-align: center !important; margin-bottom: 5px !important; font-weight: bold !important; }
        h4 { font-size: 1.1em !important; font-weight: bold !important; margin: 15px 0 5px 0 !important; border-bottom: 1px solid #eee !important; }
        table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 10px !important; font-size: 0.9em !important; }
        td { padding: 5px 8px !important; border: 1px solid #000 !important; vertical-align: middle !important; }
        .checkbox { display: inline-block !important; width: 14px !important; height: 14px !important; border: 1px solid #000 !important; margin-right: 5px !important; text-align: center !important; line-height: 12px !important; font-size: 10px !important; vertical-align: middle !important; }
        .checked::before { content: "✓" !important; font-weight: bold !important; }
        .note { font-size: 0.85em !important; text-align: center !important; font-style: italic !important; margin: 10px 0 !important; }
        @media print { .no-print { display: none !important; } .print-container { padding: 10px !important; } }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo"><img src={logo} alt="Logo" /></div>
          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>INJECTION FORM - FOR ESCORT</h6>
          </div>

          {/* Top Meta Info */}
          <table style={{ marginTop: '40px' }}>
            <tbody>
              <tr>
                <td>Standard Name: <strong>{info.standard_name}</strong></td>
                <td>Document #: <strong>{info.document_no}</strong></td>
              </tr>
              <tr>
                <td>Revision #: <strong>{info.revision_no}</strong></td>
                <td>Review Date: <strong>{formatDate(info.review_date)}</strong></td>
              </tr>
              <tr>
                <td>Department: <strong>{info.department_name}</strong></td>
                <td>Issue Date: <strong>{formatDate(info.issue_date)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* Patient Information */}
          <h4>Patient Information</h4>
          <table>
            <tbody>
              <tr>
                <td>Name: <strong>{info.name}</strong></td>
                <td>
                  Gender:{" "}
                  <span className={`checkbox ${info.gender === "Male" ? "checked" : ""}`}></span> M{" "}
                  <span className={`checkbox ${info.gender === "Female" ? "checked" : ""}`}></span> F
                </td>
              </tr>
              <tr>
                <td>Contact: <strong>{info.contact}</strong></td>
                <td>DOB: <strong>{formatDate(info.dob)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* Doctor Section */}
          <p className="note">(To be filled by the on-duty doctor/consultant)</p>
          <table>
            <thead>
              <tr style={{ background: '#f2f2f2', fontWeight: 'bold' }}>
                <td>Medicine</td>
                <td>Administered</td>
                <td>Dose</td>
                <td>Route</td>
              </tr>
            </thead>
            <tbody>
              {medicineList.map(({ key, label, type }) => {
                const isYes = onDuty[key] === "Yes";
                const dose = onDuty[`${key}_dose`] || "-";
                const route = onDuty[`${key}_type`] || "";

                return (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      <span className={`checkbox ${isYes ? "checked" : ""}`}></span> Yes{" "}
                      <span className={`checkbox ${onDuty[key] === "No" ? "checked" : ""}`}></span> No
                    </td>
                    <td>{dose}</td>
                    <td>
                      {type === "Oral" ? "Oral" : (
                        <>
                          <span className={`checkbox ${route === "IV" ? "checked" : ""}`}></span> IV{" "}
                          <span className={`checkbox ${route === "IM" ? "checked" : ""}`}></span> IM
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={4}>Doctor Remarks: <strong>{onDuty.doctor_remarks}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* Doctor Sig Block */}
          <table>
            <tbody>
              <tr>
                <td style={{ width: '30%' }}>Doctor Signature:</td>
                <td>Name: <strong>{onDuty.doctor_name}</strong></td>
                <td>Desig: <strong>{onDuty.doctor_designation}</strong></td>
              </tr>
              <tr>
                <td>Place: <strong>{onDuty.doctor_place}</strong></td>
                <td colSpan={2}>Date/Time: <strong>{formatDate(onDuty.created_at)} {formatTime(onDuty.created_at)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* RN Section */}
          <p className="note" style={{ marginTop: '20px' }}>(To be filled by the Assigned RN / Dispenser)</p>
          <table>
            <thead>
              <tr style={{ background: '#f2f2f2', fontWeight: 'bold' }}>
                <td>Medicine</td>
                <td>Administered</td>
                <td>Dose</td>
                <td>Route</td>
              </tr>
            </thead>
            <tbody>
              {medicineList.map(({ key, label, type }) => {
                const rnKey = `rn_${key}`;
                const isYes = rn[rnKey] === "Yes";
                const dose = rn[`${rnKey}_dose`] || "-";
                const route = rn[`${rnKey}_type`] || "";

                return (
                  <tr key={`rn_${key}`}>
                    <td>{label}</td>
                    <td>
                      <span className={`checkbox ${isYes ? "checked" : ""}`}></span> Yes{" "}
                      <span className={`checkbox ${rn[rnKey] === "No" ? "checked" : ""}`}></span> No
                    </td>
                    <td>{dose}</td>
                    <td>
                      {type === "Oral" ? "Oral" : (
                        <>
                          <span className={`checkbox ${route === "IV" ? "checked" : ""}`}></span> IV{" "}
                          <span className={`checkbox ${route === "IM" ? "checked" : ""}`}></span> IM
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={4}>RN Remarks: <strong>{rn.rn_remarks}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* RN Sig Block */}
          <table>
            <tbody>
              <tr>
                <td style={{ width: '30%' }}>RN/Dispenser Signature:</td>
                <td>Name: <strong>{rn.rn_name}</strong></td>
                <td>Desig: <strong>{rn.rn_designation}</strong></td>
              </tr>
              <tr>
                <td>Place: <strong>{rn.rn_place}</strong></td>
                <td colSpan={2}>Date/Time: <strong>{formatDate(rn.created_at)} {formatTime(rn.created_at)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* {hasFinanceInfo && (
          <>
            <h4 style={{ marginTop: '20px' }}>Finance Information</h4>
            <table>
              <tbody>
                <tr>
                  <td style={{ background: '#f2f2f2', width: '25%' }}>Amount:</td>
                  <td><strong>{info.amount || " "}</strong></td>
                  <td style={{ background: '#f2f2f2', width: '25%' }}>Payment Status:</td>
                  <td><strong>{info.payment_status || " "}</strong></td>
                </tr>
                <tr>
                  <td style={{ background: '#f2f2f2' }}>Reference No:</td>
                  <td><strong>{info.reference_no || " "}</strong></td>
                  <td style={{ background: '#f2f2f2' }}>Payment Date:</td>
                  <td><strong>{formatDate(info.payment_date)}</strong></td>
                </tr>
              </tbody>
            </table>
          </>
        )} */}
        </div>
      </PrintWrapper>
    </>
  );
}